import { HttpContext } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { SchoolCoordinator } from "@platform-app/app/core/api/models";
import { SchoolService } from "@platform-app/app/core/api/services";
import { SUPPRESS_ERROR_MESSAGE } from "@platform-app/app/core/errors/interceptors";
import { GENERIC_ERROR_MESSAGE } from "@platform-app/app/core/errors/messages";
import { StateToDefaultResetter } from "@platform-app/app/shared/common/store/state-to-default-resetter/state-to-default-resetter";
import {
  EnsureSchoolCoordinatorsLoaded,
  ReloadSchoolCoordinators,
} from "@platform-app/app/shared/students/store/school-coordinators.actions";
import { MessageService } from "primeng/api";
import { catchError, of, tap } from "rxjs";

export interface SchoolCoordinatorsStateModel {
  coordinators: SchoolCoordinator[];
  loaded: boolean;
  loading: boolean;
}

const DEFAULT_STATE: SchoolCoordinatorsStateModel = {
  coordinators: [],
  loaded: false,
  loading: false,
};

@State<SchoolCoordinatorsStateModel>({
  name: "SchoolCoordinators",
  defaults: DEFAULT_STATE,
})
@Injectable()
export class SchoolCoordinatorsState extends StateToDefaultResetter<SchoolCoordinatorsStateModel> {
  private readonly schoolService = inject(SchoolService);
  private readonly messageService = inject(MessageService);

  constructor() {
    super(DEFAULT_STATE);
  }

  @Selector()
  static coordinators(state: SchoolCoordinatorsStateModel) {
    return state.coordinators;
  }

  @Selector()
  static loaded(state: SchoolCoordinatorsStateModel) {
    return state.loaded;
  }

  @Action(EnsureSchoolCoordinatorsLoaded)
  ensureLoaded(ctx: StateContext<SchoolCoordinatorsStateModel>) {
    const { loaded, loading } = ctx.getState();

    if (loaded || loading) return;

    return this.load(ctx);
  }

  @Action(ReloadSchoolCoordinators)
  reload(ctx: StateContext<SchoolCoordinatorsStateModel>) {
    if (ctx.getState().loading) return;

    return this.load(ctx);
  }

  private load(ctx: StateContext<SchoolCoordinatorsStateModel>) {
    ctx.patchState({ loading: true });

    return this.schoolService
      .getSchoolCoordinators(
        {},
        new HttpContext().set(SUPPRESS_ERROR_MESSAGE, true),
      )
      .pipe(
        tap((response) =>
          ctx.patchState({
            coordinators: response.coordinators,
            loaded: true,
            loading: false,
          }),
        ),
        catchError(() => {
          ctx.patchState({ loading: false });
          this.messageService.add(GENERIC_ERROR_MESSAGE);

          return of(undefined);
        }),
      );
  }
}
